import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import api from '../services/api.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  CreditCardIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Types
interface Debt {
  id: string;
  description: string;
  type:
    | 'loan'
    | 'financing'
    | 'account_payable'
    | 'consortium'
    | 'real_estate'
    | 'vehicle'
    | 'other';
  amount: number;
  remainingAmount: number;
  interestRate: number;
  startDate: string;
  dueDate: string;
  status: 'active' | 'paid' | 'overdue' | 'renegotiated';
  creditor: string;
}

const Debts: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('active');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [debtToDelete, setDebtToDelete] = useState<string | null>(null);

  const {
    data: debts,
    isLoading,
    error,
    refetch,
  } = useQuery<Debt[]>(
    'debts',
    async () => {
      const response = await api.get('/debts');
      return response.data;
    },
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Filter debts based on search, type, and status
  const filteredDebts =
    debts?.filter(debt => {
      const matchesSearch =
        debt.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        debt.creditor.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = selectedType === 'all' || debt.type === selectedType;
      const matchesStatus = selectedStatus === 'all' || debt.status === selectedStatus;
      return matchesSearch && matchesType && matchesStatus;
    }) || [];

  const handleDelete = async (id: string) => {
    setDebtToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (debtToDelete) {
      try {
        await api.delete(`/debts/${debtToDelete}`);
        refetch();
        setShowDeleteModal(false);
        setDebtToDelete(null);
      } catch (error) {
        console.error('Erro ao excluir dívida:', error);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDebtToDelete(null);
  };

  // Calculate summary data
  const activeDebts = debts?.filter(debt => debt.status === 'active') || [];
  const renegotiatedDebts = debts?.filter(debt => debt.status === 'renegotiated') || [];
  const overdueDebts = debts?.filter(debt => debt.status === 'overdue') || [];

  // Include renegotiated debts in active amount calculation
  const totalActiveAmount = [...activeDebts, ...renegotiatedDebts].reduce(
    (sum, debt) => sum + debt.remainingAmount,
    0
  );
  const totalOverdueAmount = overdueDebts.reduce((sum, debt) => sum + debt.remainingAmount, 0);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Get next due date
  const getNextDueDate = () => {
    const activeDueDates = activeDebts
      .map(debt => new Date(debt.dueDate))
      .sort((a, b) => a.getTime() - b.getTime());

    return activeDueDates.length > 0 ? activeDueDates[0].toLocaleDateString('pt-BR') : 'N/A';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        Erro ao carregar dívidas. Por favor, tente novamente.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <CreditCardIcon className="h-8 w-8 mr-3 text-primary-600" />
            Gestão de Dívidas
          </h1>
          <p className="text-gray-600 mt-2">Controle seus financiamentos e empréstimos</p>
        </div>
        <button
          onClick={() => navigate('/debts/new')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Dívida
        </button>
      </div>

      {/* Summary Cards (responsive, consolidated to avoid overflow) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Consolidated counters card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[120px] flex items-center">
          <div className="flex items-center w-full">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4 w-full">
              <p className="text-sm font-medium text-gray-600 mb-3">Resumo</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-lg font-bold text-gray-900">{debts?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ativas</p>
                  <p className="text-lg font-bold text-gray-900">{activeDebts.length}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Em Atraso</p>
                  <p className="text-lg font-bold text-gray-900">{overdueDebts.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overdue amount card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[120px] flex items-center">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4 min-w-0">
              <p className="text-sm font-medium text-gray-600">Valor em Atraso</p>
              <p className="text-2xl font-bold text-red-600 break-words whitespace-normal leading-tight">
                {formatCurrency(totalOverdueAmount)}
              </p>
            </div>
          </div>
        </div>

        {/* Total active amount card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[120px] flex items-center">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BuildingLibraryIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4 min-w-0">
              <p className="text-sm font-medium text-gray-600">Valor Total Restante</p>
              <p className="text-2xl font-bold text-gray-900 break-words whitespace-normal leading-tight">
                {formatCurrency(totalActiveAmount)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por descrição ou credor..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Type Filter */}
            <select
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
            >
              <option value="all">Todos os Tipos</option>
              <option value="loan">Empréstimo</option>
              <option value="financing">Financiamento</option>
              <option value="account_payable">Contas a Pagar</option>
              <option value="consortium">Consórcio</option>
              <option value="real_estate">Imóvel</option>
              <option value="vehicle">Veículo</option>
              <option value="other">Outros</option>
            </select>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Ativo</option>
              <option value="paid">Pago</option>
              <option value="overdue">Em Atraso</option>
              <option value="renegotiated">Renegociado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Debts Grid */}
      {filteredDebts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDebts.map(debt => (
            <div
              key={debt.id}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{debt.description}</h3>
                  <p className="text-sm text-gray-500">{debt.creditor}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(debt.status)}`}
                >
                  {formatStatus(debt.status)}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Valor Original</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(debt.amount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Valor Restante</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(debt.remainingAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Taxa de Juros</span>
                  <span className="font-semibold text-gray-900">{debt.interestRate}% a.m.</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Vencimento</span>
                  <span
                    className={`font-semibold ${
                      debt.status === 'overdue' ? 'text-red-600' : 'text-gray-900'
                    }`}
                  >
                    {new Date(debt.dueDate).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/debts/${debt.id}`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Ver
                </button>
                <button
                  onClick={() => navigate(`/debts/${debt.id}/edit`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Editar
                </button>
                {hasPermission('debts.delete') && (
                  <button
                    onClick={() => handleDelete(debt.id)}
                    className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma dívida encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedType !== 'all' || selectedStatus !== 'all'
              ? 'Tente ajustar os filtros de busca.'
              : 'Comece criando uma nova dívida.'}
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-600" />
              <h3 className="text-lg font-medium text-gray-900 mt-2">Confirmar Exclusão</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Tem certeza que deseja excluir esta dívida? Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Debts;
